import type { HandoffProtectionDecision } from '../../../../../../../../libs/PeerCore/src/streamRecovery/contracts';
import type { HandoffProtectionPolicy } from '../../../../../../../../libs/PeerCore/src/streamRecovery/interfaces';
import { HANDOFF_HEALTHY_HOLD_MS, FRAME_EVIDENCE_STALE_MS } from '../../../../../../../../libs/PeerCore/src/streamRecovery/policy';

/** Temporary bitrate-only handoff protection; every other recovery constraint keeps its owner. */
export class TemporaryHandoffProtection implements HandoffProtectionPolicy {
  private epoch = 0;
  private cap: number | undefined;
  private healthySince: number | undefined;
  private lastHealthyAt: number | undefined;

  observe(input: Parameters<HandoffProtectionPolicy['observe']>[0]): HandoffProtectionDecision {
    const { path, nowMs } = input;
    if (path.current.epoch < this.epoch) return this.decision('awaiting-evidence');
    if (path.changed && path.current.epoch > this.epoch) {
      this.epoch = path.current.epoch;
      this.healthySince = undefined;
      this.lastHealthyAt = undefined;
      if (this.validCap(input.currentMaxBitrateBps) && this.validCap(input.hardCutMaxBitrateBps)) {
        this.cap = Math.min(this.cap ?? Infinity, input.currentMaxBitrateBps, input.hardCutMaxBitrateBps);
      }
      return this.decision('path-change');
    }
    if (this.cap === undefined) return this.decision('unchanged');
    if (this.validCap(input.currentMaxBitrateBps)) this.cap = Math.min(this.cap, input.currentMaxBitrateBps);
    if (!input.evidenceFresh || !input.healthy || path.current.selection !== 'selected' || !Number.isFinite(nowMs)) {
      this.healthySince = undefined;
      this.lastHealthyAt = undefined;
      return this.decision('awaiting-evidence');
    }
    if (this.lastHealthyAt !== undefined && (nowMs < this.lastHealthyAt || nowMs - this.lastHealthyAt >= FRAME_EVIDENCE_STALE_MS)) {
      this.healthySince = undefined;
    }
    this.lastHealthyAt = nowMs;
    this.healthySince ??= nowMs;
    if (nowMs - this.healthySince < HANDOFF_HEALTHY_HOLD_MS) return this.decision('settling');
    this.cap = undefined;
    this.healthySince = undefined;
    return this.decision('healthy');
  }

  reset(): void { this.epoch = 0; this.cap = undefined; this.healthySince = undefined; this.lastHealthyAt = undefined; }
  private validCap(value: number): boolean { return Number.isFinite(value) && value > 0; }
  private decision(reason: HandoffProtectionDecision['reason']): HandoffProtectionDecision {
    return { epoch: this.epoch, active: this.cap !== undefined, ...(this.cap !== undefined ? { maxBitrateBps: this.cap } : {}), reason };
  }
}

